import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import SouthRounded from '@mui/icons-material/SouthRounded';

export interface FunnelStep {
  key: string;
  label: string;
  value: number | null;
  /** What happened to those who did not reach the next step, e.g. "not approved". */
  lossLabel?: string | null;
}

export interface FunnelChartProps {
  steps: FunnelStep[];
  formatValue: (value: number) => string;
}

const pct = (part: number, whole: number) => `${((100 * part) / whole).toFixed(1)}%`;

/**
 * Steps from first to last, each a bar scaled to the first, with what carried
 * on and what was lost between each pair.
 *
 * Values sit in their own column, right-aligned, so they can be read down the
 * funnel like a table; each also shows its share of the first step. Between
 * steps, a line says how many carried on and how many were lost, and why —
 * the drop-off is usually the reason anyone is looking.
 */
export function FunnelChart({ steps, formatValue }: FunnelChartProps) {
  const head = steps[0]?.value ?? null;
  const columns = 'minmax(88px, 26%) minmax(0, 1fr) minmax(72px, auto)';

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: columns, columnGap: 1.5, alignItems: 'center' }}>
      {steps.map((step, index) => {
        const next = steps[index + 1];
        const value = step.value;
        const share = head && value !== null ? (100 * value) / head : null;
        const carried = next && value && next.value !== null ? next.value : null;

        return (
          <React.Fragment key={step.key}>
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, textAlign: 'right' })}>
              {step.label}
            </Typography>

            <Tooltip
              placement="top"
              title={
                value === null
                  ? `${step.label}: no data`
                  : `${step.label}: ${formatValue(value)}${head && index > 0 ? ` · ${pct(value, head)} of ${steps[0].label}` : ''}`
              }
            >
              <Box
                sx={(t) => ({
                  height: 22,
                  borderRadius: `${t.ds.radius.xs}px`,
                  bgcolor: t.tokens.surface.sunken,
                  overflow: 'hidden',
                })}
              >
                <Box
                  sx={(t) => ({
                    height: '100%',
                    width: share === null ? 0 : `${Math.max(share > 0 ? 1 : 0, Math.min(100, share))}%`,
                    borderRadius: `${t.ds.radius.xs}px`,
                    bgcolor: t.tokens.brand.primary,
                    transition: `width ${t.ds.motion.duration.slow}ms ${t.ds.motion.easing.standard}`,
                  })}
                />
              </Box>
            </Tooltip>

            <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
              <Typography
                component="span"
                variant="body2"
                sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
              >
                {value === null ? '—' : formatValue(value)}
              </Typography>
              {index > 0 && share !== null && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={(t) => ({ ml: 0.75, color: t.tokens.text.tertiary, fontVariantNumeric: 'tabular-nums' })}
                >
                  {share.toFixed(0)}%
                </Typography>
              )}
            </Box>

            {next && (
              <>
                <Box />
                <Box
                  sx={(t) => ({
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    py: 0.5,
                    color: t.tokens.text.tertiary,
                  })}
                >
                  <SouthRounded sx={(t) => ({ fontSize: t.typography.caption.fontSize })} />
                  <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {carried !== null && value ? (
                      <>
                        <Box component="strong" sx={(t) => ({ color: t.tokens.text.secondary })}>
                          {pct(carried, value)}
                        </Box>{' '}
                        carried on
                        {value - carried > 0 && (
                          <>
                            {' · '}
                            <Box component="strong" sx={(t) => ({ color: t.tokens.text.secondary })}>
                              {formatValue(value - carried)}
                            </Box>{' '}
                            {step.lossLabel ?? 'lost'}
                          </>
                        )}
                      </>
                    ) : (
                      'No data for this step'
                    )}
                  </Typography>
                </Box>
                <Box />
              </>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}
